import React from "react";
import PropTypes from "prop-types";
import Select from "react-select";

import getAssetAttachment from '@api/v4/assets/attachments';

import ClipboardCopy from "@components/common/clipboard_copy/main";
import AttachmentSidebar from '../shared/attachment_sidebar/main';
import VisibleAttachment from "../shared/visible_attachment/main";

import "./styles/main.scss";

class AssetEmbed extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      attachments: [],
      visibleAttachment: null,
      selectedFormat: {},
      selectedWidth: '',
      selectedHeight: '',
      loading: true
    };

    this.activeRequests = [];
  }

  /* NOTE :: When you have a parent modal component that grabs the asset info
  you won't need to grab the attachments on each tab, you can grab them when
  you load the modal and then just pass them down. */
  componentDidMount() {
    let params = {
      assetKey: this.props.assetKey,
      fields: "best_metadata,other_metadata,download_url,thumbnail_url,view_thumbnail_retina,extension,thumbnailed,conversion_options"
    };

    params = BFG.manifestDigest ? { ...params, digest: BFG.manifestDigest } : params;
    params = this.props.watermarkResourceKey ? { ...params, watermark_resource_key: this.props.watermarkResourceKey } : params;

    getAssetAttachment({
      assetKey: this.props.assetKey,
      params
    }, this.updateFetchControllers).then((response) => {
      const sortedAttachments = response.data.sort((a, b) => (
        a.attributes.position > b.attributes.position ? 1 : -1
      ));
      const firstAttachment = sortedAttachments[0];
      // const fileFormat = firstAttachment.attributes.extension || '';

      this.setState({
        attachments: sortedAttachments,
        loading: false,
        visibleAttachment: firstAttachment,
        selectedWidth: firstAttachment.attributes.width || 0,
        selectedHeight: firstAttachment.attributes.height || 0
      });
    }).catch(() => {
      this.setState({ loading: false });
    });
  }

  componentWillUnmount() {
    this.abortFetchControllers();
  }

  get iframe() {
    if (!this.state.visibleAttachment) { return null; }

    return `<iframe src='https://brandfolder.com/${this.props.brandfolderSlug}/attachments/embed/${this.state.visibleAttachment.id}?resource_type=Brandfolder&frame=auto&animated=false&loop=false&muted=false&autoplay=false' frameborder='0' allowfullscreen></iframe>`;
  }

  get formatOptions() {
    if (!this.state.visibleAttachment) { return null; }

    const formatOptions = [
      { value: "auto", label: "AUTO" },
      { value: "jpg", label: "JPG" },
      { value: "png", label: "PNG" },
      { value: "pdf", label: "PDF" },
      { value: "gif", label: "GIF" },
      { value: "svg", label: "SVG" },
    ];
    const visibleExtension = this.state.visibleAttachment.attributes.extension;

    if (!formatOptions.find((option) => option.value === visibleExtension)) {
      formatOptions.push({
        value: visibleExtension,
        label: visibleExtension.toUpperCase(),
      });
    }
    return formatOptions;
  }


  updateFetchControllers = (fetchController) => {
    this.activeRequests = [...this.activeRequests, fetchController];
  };

  abortFetchControllers = () => {
    this.activeRequests.forEach((fetchController) => {
      if (!fetchController.signal.aborted) {
        fetchController.abort();
      }
    });
  };


  determineDefaultExtension = (attachmentExtension) => {
    const lowerAttachmentExtension = attachmentExtension.toLowerCase();
    this.setState({
      selectedFormat: {
        value: lowerAttachmentExtension,
        label: lowerAttachmentExtension.toUpperCase()
      }
    });
    return lowerAttachmentExtension;
  }

  stripExtensionFromName = (attachmentExtension, attachmentName) => {
    const extensionFromName = attachmentName.split('.').pop();
    // strip file extension from filename
    if (attachmentExtension && attachmentExtension.toLowerCase() === extensionFromName.toLowerCase()) {
      const attachmentNameUpdated = attachmentName.split('.');
      attachmentNameUpdated.pop();
      return attachmentNameUpdated.join('.');
    }
    return attachmentName;
  }

  link = () => {
    if (!this.state.visibleAttachment) { return null; }

    const { assetKey, assetName, cdnKey } = this.props;
    const { selectedFormat, selectedHeight, selectedWidth, visibleAttachment } = this.state;
    const {
      extension: attachmentExtension,
      filename: attachmentName,
      height,
      position,
      width,
    } = visibleAttachment.attributes;

    const isV2 = BFG.smartCDNSource === "fastly_v2";
    const attachmentNameStripped = this.stripExtensionFromName(attachmentExtension, attachmentName);
    const assetNameClean = assetName.replace(/ /g, "_").replace(/[^-a-zA-Z0-9_ ]+/gi, '');
    const attachmentNameClean = attachmentNameStripped.replace(/ /g, "_").replace(/[^-a-zA-Z0-9_ ]+/gi, '');
    const attachmentExtensionClean = attachmentExtension || '';
    const attachmentExtensionSelected = selectedFormat.value ? selectedFormat.value : this.determineDefaultExtension(attachmentExtensionClean);
    // NOTE that for v2 we don't use extensions, they're confusing with their intent. format=<> conveys desire
    const assetRootLinkEnding = isV2 ? "" : `.${attachmentExtensionSelected}`;
    const assetRootLink = `${BFG.smartCDNRoot}/${cdnKey}/as/${assetKey}/${encodeURI(assetNameClean)}${assetRootLinkEnding}`;
    const attachmentRootLink = `${BFG.smartCDNRoot}/${cdnKey}/at/${visibleAttachment.id}/${encodeURI(attachmentNameClean)}${assetRootLinkEnding}`;
    let path = '';
    let pathPosition = '';
    const getSeparator = () => (path.includes('?') ? '&' : '?');

    if (isV2 && attachmentExtensionSelected !== attachmentExtensionClean) {
      path += `${getSeparator()}format=${attachmentExtensionSelected}`;
    }

    if (width && selectedWidth !== width && selectedWidth !== '') {
      path += `${getSeparator()}width=${selectedWidth}`;
    }

    if (height && selectedHeight !== height && selectedHeight !== '') {
      path += `${getSeparator()}height=${selectedHeight}`;
    }

    if (position > 0) {
      pathPosition = `${getSeparator()}position=${position + 1}`;
    }

    return {
      assetLink: `${assetRootLink}${path}${pathPosition}`,
      attachmentLink: `${attachmentRootLink}${path}`,
    };
  }

  maintainImageSizeRatio = (e) => {
    let { height, width } = this.state.visibleAttachment.attributes;
    const updatedValue = parseInt(e.target.value, 10);
    const aspectRatio = width / height;
    const isWidth = e.target.className && e.target.className.indexOf("asset-width") > -1;

    if (!Number.isInteger(updatedValue)) {
      width = '';
      height = '';
    } else if (isWidth) {
      width = updatedValue;
      height = Math.round(width / aspectRatio);
    } else {
      height = updatedValue;
      width = Math.round(height * aspectRatio);
    }

    this.setState({ selectedWidth: width, selectedHeight: height });
  }

  updateActiveImage = ({ id: attachmentId }) => {
    if (attachmentId !== this.state.visibleAttachment.id) {
      this.setState((prevState) => {
        const visible = prevState.attachments.find((attachment) => attachment.id === attachmentId);
        return {
          visibleAttachment: visible,
          selectedFormat: {},
          selectedWidth: visible.attributes.width || 0,
          selectedHeight: visible.attributes.height || 0,
        };
      });
    }
  }

  render() {
    const { attachments, loading, visibleAttachment } = this.state;
    const isVideo = visibleAttachment && visibleAttachment.attributes && visibleAttachment.attributes.mimetype && visibleAttachment.attributes.mimetype.indexOf("video") > -1;
    const activeCard = visibleAttachment
      ? { id: visibleAttachment.id, type: 'attachment' }
      : null;

    return (
      <div className="flex-wrapper asset-embed-component">
        <AttachmentSidebar
          attachments={attachments.length ? attachments : null}
          updateActiveCard={this.updateActiveImage}
          activeCard={activeCard}
        />
        {loading ? (
          <div className="flex-two-thirds no-similar-details loader-container">
            <video
              className="brandfolder-loader"
              width="90"
              height="90"
              poster="https://cdn.brandfolder.io/4OQZ5PW1/as/pxmju5-qfr48-21nt37/generic-loader.gif"
            />
          </div>
        ) : (
          <div className="modal-asset-two-thirds flex-two-thirds embed-details bf-scroll-element">
            {!BFG.hasFeature('embed_asset') && (
              <div className="embed-details__no-support">
                <div className="embed-details__no-support--details flex-two-thirds">
                  <h3>{t('Your account doesn’t currently support embedding.')}</h3>
                  <p>{t('Embedding with CDN (Content Delivery Network) Links allow you to embed assets on external websites, while housing the single source of truth version in')} {this.props.libraryName}.</p>
                  <p>{t('Do you have special requirements? Your account manager can answer questions and get you a quote for the embed feature based on your specific needs.')}</p>
                  <a href={`mailto:${this.props.supportEmail}`}>
                    <button type="button" className="button primary">{t('Get a Quote')}</button>
                  </a>
                </div>
                <div className="embed-details__no-support--list flex-one-third">
                  <p>{t('With CDN embedding, you can:')}</p>
                  <ul>
                    <li>
                      <span className="bff-confirmation" />
                      {t('Edit or replace assets in')} {this.props.libraryName} {('and see the changes reflected on external sites')}
                    </li>
                    <li>
                      <span className="bff-confirmation" />
                      {t('Track asset usage outside of')} {this.props.libraryName}
                    </li>
                    <li>
                      <span className="bff-confirmation" />
                      {t('Ensure assets that live in marketing campaigns and digital channels are never out of date')}
                    </li>
                  </ul>
                </div>
              </div>
            )}
            <div className={!BFG.hasFeature('embed_asset') ? 'disabled' : ''}>
              {visibleAttachment && visibleAttachment.attributes && (
                <>
                  <VisibleAttachment
                    extension={visibleAttachment.attributes.extension}
                    filename={visibleAttachment.attributes.filename}
                    height={visibleAttachment.attributes.height}
                    size={visibleAttachment.attributes.size}
                    thumbnail={visibleAttachment.attributes.thumbnail_url}
                    width={visibleAttachment.attributes.width}
                  />
                  <div className="embed-text">
                    <h3>{t('Embed this asset')}</h3>
                    <p>{t('By embedding this link you are acknowledging that it is accessible to anyone from where it is embedded.')}</p>
                  </div>
                  <hr />
                  {isVideo ? (
                    <div className="cdn-builder">
                      <div className="copy-link-container">
                        <span className="bf-label bf-label--primary">{t('Video Embed Code ')}</span>
                        <ClipboardCopy
                          textToCopy={this.iframe}
                          check
                          showFeedbackAsTooltip={false}
                        >
                          <span className="bff-copy" />
                          {t(' Copy to clipboard')}
                        </ClipboardCopy>
                      </div>
                      <pre className="embed-link">
                        <code>
                          <span className="embed-link">{this.iframe}</span>
                        </code>
                      </pre>
                      <div className="copy-link-container">
                        <span className="bf-label bf-label--primary">{t('Asset CDN Link')}</span>
                        <ClipboardCopy
                          textToCopy={this.link().assetLink}
                          check
                          showFeedbackAsTooltip={false}
                        >
                          <span className="bff-copy" />
                          {t('Copy link')}
                        </ClipboardCopy>
                      </div>
                      <pre className="embed-link">
                        <code>
                          <span className="embed-link">{this.link().assetLink}</span>
                        </code>
                      </pre>
                      <p>{t('See our ')}
                        <a
                          href="https://help.brandfolder.com/hc/en-us/articles/360005174134-Embedding-Assets-with-Smart-CDN"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('CDN documentation')}
                        </a>
                      </p>
                    </div>
                  ) : (
                    <div className="cdn-builder">
                      <div className="copy-link-container">
                        <div className="cdn-title">
                          <span className="bf-label bf-label--primary">{t('Asset CDN Link')}</span>
                        </div>
                        <ClipboardCopy
                          textToCopy={this.link().assetLink}
                          check
                          showFeedbackAsTooltip={false}
                        >
                          <span className="bff-copy" />
                          {t('Copy link')}
                        </ClipboardCopy>
                      </div>
                      <pre className="embed-link">
                        <code>
                          <span className="embed-link">{this.link().assetLink}</span>
                        </code>
                      </pre>
                      <div className="copy-link-container">
                        <div className="cdn-title">
                          <span className="bf-label bf-label--primary">{t('Attachment CDN Link')}</span>
                        </div>
                        <ClipboardCopy
                          textToCopy={this.link().attachmentLink}
                          check
                          showFeedbackAsTooltip={false}
                        >
                          <span className="bff-copy" />
                          {t('Copy link')}
                        </ClipboardCopy>
                      </div>
                      <pre className="embed-link">
                        <code>
                          <span className="embed-link">{this.link().attachmentLink}</span>
                        </code>
                      </pre>
                      <div className="embed-input">
                        <div className="format-select">
                          <label className="brandfolder-label">{t('Format')}</label>
                          <Select
                            value={this.state.selectedFormat}
                            options={this.formatOptions}
                            clearable={false}
                            searchable={false}
                            onChange={(selected) => this.setState({ selectedFormat: selected })}
                          />
                        </div>
                        <div className="resize-dimension-container">
                          <div className="width-input">
                            <label className="brandfolder-label">{t('Width (px)')}</label>
                            <input
                              className="asset-width brandfolder-input"
                              value={this.state.selectedWidth}
                              onChange={this.maintainImageSizeRatio}
                            />
                          </div>
                          <span className="bff-private" />
                          <div className="height-input">
                            <label className="brandfolder-label">{t('Height (px)')}</label>
                            <input
                              className="asset-height brandfolder-input"
                              value={this.state.selectedHeight}
                              onChange={this.maintainImageSizeRatio}
                            />
                          </div>
                        </div>
                      </div>
                      <p>{t('See our ')}
                        <a
                          href="https://help.brandfolder.com/hc/en-us/articles/360005174134-Embedding-Assets-with-Smart-CDN"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('CDN documentation')}
                        </a>
                      </p>
                      <p><strong>{t('AUTO')}</strong> {t('Automatically chooses the best format (WEBP or PNG) to optimize the load time of images based on the users browser.')}</p>
                    </div>
                  )}
                  <hr />
                  <div className="api-link">
                    <div className="copy-link-container">
                      <h3>{t('API Usage')}</h3>
                    </div>
                    <p>{t('See our ')}
                      <a
                        href="https://developers.brandfolder.com"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('API documentation')}
                      </a>
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

AssetEmbed.propTypes = {
  assetKey: PropTypes.string.isRequired,
  assetName: PropTypes.string.isRequired,
  brandfolderSlug: PropTypes.string.isRequired,
  cdnKey: PropTypes.string.isRequired,
  libraryName: PropTypes.string.isRequired,
  supportEmail: PropTypes.string.isRequired,
  watermarkResourceKey: PropTypes.string
};

AssetEmbed.defaultProps = {
  watermarkResourceKey: undefined,
};

export default AssetEmbed;
